import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lightning, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError } from "../lib/utils-extra";

export default function EvaluationCycleNew() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const ninetyAgo = new Date(Date.now() - 90 * 86400 * 1000).toISOString().slice(0, 10);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState({
    name: `Cycle — ${new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}`,
    evaluation_date: today,
    period_start: ninetyAgo,
    period_end: today,
    department: "ALL",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setDepartments(data.departments || []));
  }, []);

  const launch = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.department === "ALL") delete payload.department;
      const { data } = await api.post("/evaluation-cycles", payload);
      setResult(data);
      toast.success(`${data.created_count} draft evaluation${data.created_count === 1 ? "" : "s"} created`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="evaluation-cycle-new-page">
      <button onClick={() => navigate("/evaluations")} className="text-sm text-[#475569] flex items-center gap-1">
        <ArrowLeft size={14} /> Back to evaluations
      </button>

      <div>
        <div className="small-label">Batch operation</div>
        <h1 className="font-display text-3xl font-semibold mt-1 flex items-center gap-2">
          <Lightning size={28} weight="duotone" color="#0EA5E9" /> Start a Cycle
        </h1>
        <p className="text-[#475569] text-sm mt-1">Create draft evaluations for every active employee — pick a department or run for all.</p>
      </div>

      {!result ? (
        <Card className="surface-card border-[#E2E8F0]">
          <CardContent className="p-6 space-y-5">
            <div>
              <Label className="small-label">Cycle Name</Label>
              <Input className="mt-2 h-11" value={form.name} data-testid="cycle-name-input"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="small-label">Department scope</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger className="mt-2 h-11" data-testid="cycle-department-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All departments</SelectItem>
                  {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="small-label">Evaluation Date</Label>
                <Input type="date" className="mt-2" value={form.evaluation_date} data-testid="cycle-eval-date"
                       onChange={(e) => setForm({ ...form, evaluation_date: e.target.value })} />
              </div>
              <div>
                <Label className="small-label">Period Start</Label>
                <Input type="date" className="mt-2" value={form.period_start} data-testid="cycle-period-start"
                       onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
              </div>
              <div>
                <Label className="small-label">Period End</Label>
                <Input type="date" className="mt-2" value={form.period_end} data-testid="cycle-period-end"
                       onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
              </div>
            </div>
            <div className="p-4 rounded-xl bg-[#F1F5F9] text-xs text-[#475569]">
              The system will skip employees whose position has no KPI template, and any who already have a draft
              for this exact period (no duplicates).
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
              <Button onClick={launch} disabled={busy || !form.name}
                      data-testid="launch-cycle-button"
                      className="bg-[#0EA5E9] hover:bg-[#0284C7]">
                {busy ? "Launching…" : `Launch cycle →`}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="surface-card border-[#E2E8F0]" data-testid="cycle-result-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle size={22} weight="duotone" color="#16A34A" />
              <div className="font-display text-xl font-semibold">{result.cycle_name}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Created" value={result.created_count} accent="#16A34A" />
              <Stat label="Skipped (duplicate)" value={result.skipped_duplicate.length} accent="#D97706" />
              <Stat label="Skipped (no template)" value={result.skipped_no_template.length} accent="#DC2626" />
            </div>

            {result.created.length > 0 && (
              <div>
                <div className="small-label mb-2">Newly created drafts</div>
                <div className="space-y-1">
                  {result.created.map((c) => (
                    <div key={c.evaluation_id} className="flex items-center justify-between p-2 rounded-lg bg-[#F8FAFC] text-sm"
                         data-testid={`cycle-created-${c.employee_id}`}>
                      <span>{c.name}</span>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/evaluations/${c.evaluation_id}`)}>
                        Open →
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.skipped_duplicate.length > 0 && (
              <div>
                <div className="small-label mb-2 flex items-center gap-1">
                  <WarningCircle size={14} color="#D97706" /> Already had a draft for this period
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {result.skipped_duplicate.map((s) => <span key={s.employee_id} className="pill">{s.name}</span>)}
                </div>
              </div>
            )}

            {result.skipped_no_template.length > 0 && (
              <div>
                <div className="small-label mb-2 flex items-center gap-1">
                  <WarningCircle size={14} color="#DC2626" /> Skipped — no KPI template for position
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {result.skipped_no_template.map((s) => (
                    <Badge key={s.employee_id} className="bg-[#DC2626]/10 text-[#DC2626] border-0" title={s.reason}>
                      {s.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setResult(null); }} data-testid="cycle-launch-another">
                Launch another
              </Button>
              <Button onClick={() => navigate("/evaluations")}
                      data-testid="cycle-back-to-evals"
                      className="bg-[#0EA5E9] hover:bg-[#0284C7]">
                Go to evaluations
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="p-3 rounded-xl border border-[#E2E8F0]">
      <div className="small-label">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
